import React, { useEffect } from "react";
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { AppProps } from "next/app";
import { Router } from "next/router";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { Toaster } from "sonner";
import { AddressFieldsApiProvider } from "@fluid-app/address-fields/address-fields-api-context";
import TranslationProvider from "@/components/providers/TranslationProvider";
import { RealtimeProvider } from "@/components/providers/RealtimeProvider";
import { createAddressFieldsAdapter } from "@/lib/address-fields-adapter";

import "@/styles/globals.css";

function MyApp({ Component, pageProps }: AppProps) {
  const [queryClient] = React.useState(() => new QueryClient());
  const [addressFieldsApi] = React.useState(createAddressFieldsAdapter);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      return;
    }
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host:
        process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      loaded: (posthog) => {
        if (process.env.NODE_ENV === "development") posthog.debug();
      },
    });

    const handleRouteChange = () => posthog?.capture("$pageview");
    Router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <AddressFieldsApiProvider value={addressFieldsApi}>
          <HydrationBoundary state={pageProps.dehydratedState}>
            <RealtimeProvider>
              <TranslationProvider>
                <Component {...pageProps} />
              </TranslationProvider>
            </RealtimeProvider>
          </HydrationBoundary>
          {process.env.NODE_ENV === "development" && <ReactQueryDevtools />}
          <Toaster position="top-center" />
        </AddressFieldsApiProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
}

export default MyApp;
